import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const PortalAuthContext = createContext({});

export const usePortalAuth = () => {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within PortalAuthProvider');
  }
  return context;
};

export const PortalAuthProvider = ({ children }) => {
  // portalUser: null | { _authOnly: true, id, email } | full client record
  const [portalUser, setPortalUser] = useState(null);
  const [portalLoading, setPortalLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Prevent double-fetch
  const initDoneRef = useRef(false);
  const profileLoadingRef = useRef(false);

  const isPasswordChangePage = location?.pathname === '/signin/primer-acceso' || location?.pathname === '/portal-cliente-cambiar-password';
  const isLoginPage = location?.pathname === '/portal-cliente-login';

  // ── Background profile loader — does NOT block shell entry ──────────────────
  const loadClientRecordBackground = useCallback(async (userId, authEmail) => {
    if (profileLoadingRef?.current) return;
    profileLoadingRef.current = true;

    // Safety timeout — 5s max
    const timeoutId = setTimeout(() => {
      console.warn('[PortalAuth] loadClientRecord timeout');
      profileLoadingRef.current = false;
      // Keep the auth-only user so shell stays visible; don't redirect
    }, 5000);

    try {
      // ── Step 1: Primary lookup by portal_user_id ─────────────────────────
      const { data: clientByUserId, error: err1 } = await supabase
        ?.from('clients')
        ?.select('id, nombre, email, telefono, company_id, portal_user_id')
        ?.eq('portal_user_id', userId)
        ?.maybeSingle();

      if (err1) {
        console.error('[PortalAuth] Error fetching client by portal_user_id:', err1?.message);
      }

      let client = clientByUserId || null;

      // ── Step 2: Email fallback ────────────────────────────────────────────
      if (!client && authEmail) {
        const { data: clientsByEmail, error: err2 } = await supabase
          ?.from('clients')
          ?.select('id, nombre, email, telefono, company_id, portal_user_id')
          ?.ilike('email', authEmail?.trim()?.toLowerCase())
          ?.order('created_at', { ascending: true })
          ?.limit(5);

        if (!err2 && clientsByEmail?.length > 0) {
          const linked = clientsByEmail?.find(c => c?.portal_user_id === userId);
          const clientByEmail = linked || clientsByEmail?.[0];
          client = clientByEmail;

          if (!clientByEmail?.portal_user_id) {
            const { error: fixErr } = await supabase
              ?.from('clients')
              ?.update({ portal_user_id: userId })
              ?.eq('id', clientByEmail?.id);
            if (!fixErr) {
              client = { ...clientByEmail, portal_user_id: userId };
            }
          }
        }
      }

      if (!client) {
        console.warn('[PortalAuth] No client record found for userId:', userId);
        if (!isPasswordChangePage) {
          await supabase?.auth?.signOut();
          setPortalUser(null);
          navigate('/portal-cliente-login', { replace: true });
        }
        return;
      }

      // ── Step 3: Access flags (non-blocking, single query) ────────────────
      let accessReq = null;
      const { data: accessByClientId } = await supabase
        ?.from('portal_access_requests')
        ?.select('requiere_cambio_password, password_temporal_activa')
        ?.eq('matched_cliente_id', client?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.maybeSingle();

      if (accessByClientId) {
        accessReq = accessByClientId;
      } else if (client?.email) {
        const { data: accessByEmail } = await supabase
          ?.from('portal_access_requests')
          ?.select('requiere_cambio_password, password_temporal_activa')
          ?.ilike('email', client?.email?.trim()?.toLowerCase())
          ?.order('created_at', { ascending: false })
          ?.limit(1)
          ?.maybeSingle();
        accessReq = accessByEmail || null;
      }

      // Upgrade the user object with full profile (preserves shell already rendered)
      setPortalUser({
        ...client,
        password_temporal_activa: accessReq?.password_temporal_activa === true,
        requiere_cambio_password: accessReq?.requiere_cambio_password === true,
      });
    } catch (err) {
      console.error('[PortalAuth] Exception loading client profile:', err);
      if (!isPasswordChangePage) {
        setPortalUser(null);
        navigate('/portal-cliente-login', { replace: true });
      }
    } finally {
      clearTimeout(timeoutId);
      profileLoadingRef.current = false;
    }
  }, [navigate, isPasswordChangePage]);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        if (!mounted) return;

        if (sessionError) {
          console.warn('[PortalAuth] getSession error:', sessionError?.message);
          // Clear stored tokens before signing out to prevent refresh token loops
          try {
            localStorage.removeItem('sb-gearcontrol-auth-token');
            Object.keys(localStorage)?.forEach(key => {
              if (key?.startsWith('sb-') && key?.includes('auth')) localStorage.removeItem(key);
            });
          } catch (_) {}
          await supabase?.auth?.signOut();
          setPortalUser(null);
          setPortalLoading(false);
          if (!isLoginPage) navigate('/portal-cliente-login', { replace: true });
          return;
        }

        if (session?.user) {
          // ── INSTANT ENTRY: set minimal auth user immediately, clear loading ──
          setPortalUser({ _authOnly: true, id: session?.user?.id, email: session?.user?.email });
          setPortalLoading(false);
          // Load full profile in background — does not block shell
          loadClientRecordBackground(session?.user?.id, session?.user?.email);
        } else {
          setPortalUser(null);
          setPortalLoading(false);
        }
      } catch (err) {
        console.error('[PortalAuth] Init error:', err);
        if (mounted) {
          setPortalUser(null);
          setPortalLoading(false);
        }
      } finally {
        if (mounted) initDoneRef.current = true;
      }
    };

    init();

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (
        event === 'SIGNED_OUT' ||
        (event === 'TOKEN_REFRESHED' && !session) ||
        (event === 'INITIAL_SESSION' && !session && !initDoneRef?.current)
      ) {
        // Clear any stale tokens to prevent refresh token error loops
        try {
          localStorage.removeItem('sb-gearcontrol-auth-token');
          Object.keys(localStorage)?.forEach(key => {
            if (key?.startsWith('sb-') && key?.includes('auth')) localStorage.removeItem(key);
          });
        } catch (_) {}
        setPortalUser(null);
        setPortalLoading(false);
        if (!isLoginPage && !isPasswordChangePage) {
          navigate('/portal-cliente-login', { replace: true });
        }
      } else if (event === 'SIGNED_IN' && session?.user) {
        // Only handle if init() did NOT already process this session
        if (!initDoneRef?.current) return;
        // Instant entry — set minimal user, then load profile in background
        setPortalUser(prev =>
          prev && !prev?._authOnly ? prev : { _authOnly: true, id: session?.user?.id, email: session?.user?.email }
        );
        setPortalLoading(false);
        loadClientRecordBackground(session?.user?.id, session?.user?.email);
      }
    });

    const handleAuthError = (event) => {
      const error = event?.detail;
      if (error?.message?.includes('Refresh Token') || error?.message?.includes('refresh_token')) {
        console.warn('[PortalAuth] Invalid refresh token detected — clearing session');
        supabase?.auth?.signOut()?.then(() => {
          if (mounted) {
            setPortalUser(null);
            setPortalLoading(false);
            if (!isLoginPage) navigate('/portal-cliente-login', { replace: true });
          }
        });
      }
    };

    window.addEventListener('supabase.auth.error', handleAuthError);

    return () => {
      mounted = false;
      subscription?.unsubscribe();
      window.removeEventListener('supabase.auth.error', handleAuthError);
    };
  }, [loadClientRecordBackground]);

  const portalSignOut = useCallback(async () => {
    await supabase?.auth?.signOut();
    setPortalUser(null);
    navigate('/portal-cliente-login', { replace: true });
  }, [navigate]);

  const markPasswordChanged = useCallback(() => {
    setPortalUser(prev => prev ? { ...prev, password_temporal_activa: false, requiere_cambio_password: false } : prev);
  }, []);

  return (
    <PortalAuthContext.Provider value={{ portalUser, portalLoading, portalSignOut, markPasswordChanged }}>
      {children}
    </PortalAuthContext.Provider>
  );
};

export default PortalAuthProvider;
